/**
 * EmailRecipientPicker — Adressbuch-Picker für den Email-Composer.
 *
 * Öffnet ein modales Suchfenster, in dem der Nutzer Adressen und deren
 * Ansprechpartner durchsuchen kann. Selektierte E-Mail-Adressen werden über
 * `onAdd(emails, target)` an den Composer zurückgegeben.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Search, Mail, User, Building2, ChevronRight, Loader2, UserPlus, Filter, EyeOff } from 'lucide-react';
import addressService from '../../services/addressService';
import addressFilterService from '../../services/addressFilterService';

const TARGETS = [
  { key: 'to',  label: 'An'  },
  { key: 'cc',  label: 'CC'  },
  { key: 'bcc', label: 'BCC' },
];

const MODI = [
  { key: 'adressbuch', label: 'Adressbuch', icon: UserPlus },
  { key: 'datenfilter', label: 'Datenfilter', icon: Filter },
];

export default function EmailRecipientPicker({ open, onClose, onAdd, onAddFilter, defaultTarget = 'to', defaultModus = 'adressbuch', hatAnEmpfaenger = false }) {
  const [modus, setModus] = useState('adressbuch');
  const [filterListe, setFilterListe] = useState([]);
  const [filterLaden, setFilterLaden] = useState(false);
  const [filterId, setFilterId] = useState('');
  const [filterErgebnis, setFilterErgebnis] = useState(null); // { filter, recipients, total_addresses, ohne_email }
  const [ergebnisLaden, setErgebnisLaden] = useState(false);
  const [anEmail, setAnEmail] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // { email: { email, label } }
  const [target, setTarget] = useState(defaultTarget);

  // Reset + Initial-Load beim Öffnen: ALLE Adressen laden (Hauptadresse + Ansprechpartner)
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected({});
    setTarget(defaultTarget);
    setModus(onAddFilter ? defaultModus : 'adressbuch');
    setFilterId('');
    setFilterErgebnis(null);
    setAnEmail('');
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await addressService.getAll({ limit: 1000 });
        const items = Array.isArray(r) ? r : (r?.items || r?.addresses || []);
        if (!cancelled) setResults(items);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, defaultTarget, defaultModus]);

  // Gespeicherte Datenfilter laden, sobald das Register „Datenfilter“ geöffnet wird
  useEffect(() => {
    if (!open || modus !== 'datenfilter' || filterListe.length) return;
    let cancelled = false;
    (async () => {
      setFilterLaden(true);
      try {
        const r = await addressFilterService.getAll();
        const liste = (r?.filters || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
        if (!cancelled) setFilterListe(liste);
      } catch {
        if (!cancelled) setFilterListe([]);
      } finally {
        if (!cancelled) setFilterLaden(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, modus, filterListe.length]);

  // Empfänger des gewählten Datenfilters laden
  useEffect(() => {
    if (!filterId) { setFilterErgebnis(null); return; }
    let cancelled = false;
    (async () => {
      setErgebnisLaden(true);
      try {
        const r = await addressFilterService.emails(filterId);
        if (!cancelled) {
          setFilterErgebnis(r);
          setAnEmail(r?.recipients?.[0]?.email || '');
        }
      } catch {
        if (!cancelled) setFilterErgebnis({ recipients: [], total_addresses: 0, ohne_email: 0, fehler: true });
      } finally {
        if (!cancelled) setErgebnisLaden(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filterId]);

  // Aufteilung: 1 Empfänger ins „An“, der Rest ins BCC (Empfänger sehen sich nicht gegenseitig).
  // Steht im Composer bereits ein „An“-Empfänger, gehen alle Treffer ins BCC.
  const filterAufteilung = useMemo(() => {
    const alle = (filterErgebnis?.recipients || []).map((r) => r.email);
    if (!alle.length) return { to: [], bcc: [] };
    if (hatAnEmpfaenger) return { to: [], bcc: alle };
    const an = alle.includes(anEmail) ? anEmail : alle[0];
    return { to: [an], bcc: alle.filter((e) => e !== an) };
  }, [filterErgebnis, anEmail, hatAnEmpfaenger]);

  const handleFilterUebernehmen = () => {
    if (!filterAufteilung.to.length && !filterAufteilung.bcc.length) return;
    onAddFilter && onAddFilter({
      to: filterAufteilung.to,
      bcc: filterAufteilung.bcc,
      filterName: filterErgebnis?.filter?.name || '',
    });
    onClose && onClose();
  };

  // Flacher Index aller verfügbaren Mail-Optionen (Adresse selbst + Ansprechpartner)
  const flatOptions = useMemo(() => {
    const out = [];
    results.forEach((addr) => {
      const displayName = addr.company_name || [addr.first_name, addr.last_name].filter(Boolean).join(' ') || 'Unbenannt';
      if (addr.email) {
        out.push({
          email: addr.email,
          label: displayName,
          subline: addr.type === 'person' ? 'Person' : (addr.company_name ? 'Firma' : 'Adresse'),
          icon: addr.type === 'person' ? 'user' : 'company',
          addressId: addr.id,
        });
      }
      (addr.contact_persons || []).forEach((cp) => {
        if (cp.email) {
          const cpName = [cp.first_name, cp.last_name].filter(Boolean).join(' ').trim() || 'Ansprechpartner';
          out.push({
            email: cp.email,
            label: cpName,
            subline: `${displayName}${cp.position ? ' · ' + cp.position : ''}`,
            icon: 'user',
            addressId: addr.id,
            isContactPerson: true,
          });
        }
      });
    });
    // Duplikate auf E-Mail-Basis entfernen
    const seen = new Set();
    return out.filter((o) => {
      const k = o.email.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [results]);

  // Clientseitiger Filter über Name, E-Mail, Subline
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return flatOptions;
    return flatOptions.filter((o) =>
      o.email.toLowerCase().includes(q) ||
      (o.label || '').toLowerCase().includes(q) ||
      (o.subline || '').toLowerCase().includes(q)
    );
  }, [flatOptions, query]);

  const toggle = (opt) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[opt.email]) delete next[opt.email];
      else next[opt.email] = opt;
      return next;
    });
  };

  const handleAdd = () => {
    const emails = Object.keys(selected);
    if (emails.length === 0) return;
    onAdd(emails, target);
    onClose && onClose();
  };

  const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every((o) => selected[o.email]);
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = { ...prev };
      if (allFilteredSelected) {
        filteredOptions.forEach((o) => { delete next[o.email]; });
      } else {
        filteredOptions.forEach((o) => { next[o.email] = o; });
      }
      return next;
    });
  };

  if (!open) return null;

  const selectedCount = Object.keys(selected).length;

  return (
    <div
      className="fixed inset-0 z-[320] flex items-center justify-center bg-black/40 px-4 pointer-events-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      data-testid="composer-address-picker"
    >
      <div className="w-full max-w-2xl max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
              {modus === 'datenfilter' ? <Filter className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">
                {modus === 'datenfilter' ? 'Empfänger aus Datenfilter' : 'Empfänger aus Adressbuch'}
              </h3>
              <p className="text-[11px] text-gray-500">
                {modus === 'datenfilter'
                  ? 'Ein Empfänger kommt ins „An“, alle weiteren ins BCC'
                  : 'Suche nach Adresse, Firma oder Ansprechpartner'}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-md hover:bg-gray-100 inline-flex items-center justify-center text-gray-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Register: Adressbuch | Datenfilter */}
        {onAddFilter && (
          <div className="px-4 pt-2 border-b border-gray-100 flex items-center gap-1" data-testid="composer-picker-modus">
            {MODI.map((m) => {
              const Icon = m.icon;
              const aktiv = modus === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setModus(m.key)}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 border-b-2 -mb-px transition-colors ${
                    aktiv ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                  data-testid={`composer-picker-modus-${m.key}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {m.label}
                </button>
              );
            })}
          </div>
        )}

        {modus === 'datenfilter' ? (
          <>
            <div className="px-4 py-3 border-b border-gray-100 space-y-2">
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Datenfilter</label>
              {filterLaden ? (
                <div className="flex items-center text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Datenfilter werden geladen …</div>
              ) : filterListe.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Noch keine gespeicherten Datenfilter. Leg sie unter Adressen › Datenfilter an.
                </p>
              ) : (
                <select
                  value={filterId}
                  onChange={(e) => setFilterId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-white"
                  data-testid="composer-picker-filter-select"
                >
                  <option value="">Datenfilter wählen …</option>
                  {filterListe.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}{typeof f.result_count === 'number' ? ` (${f.result_count} Adressen)` : ''}
                    </option>
                  ))}
                </select>
              )}
              {filterErgebnis && !ergebnisLaden && filterAufteilung.to.length > 0 && filterErgebnis.recipients.length > 1 && (
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Empfänger im „An“</label>
                  <select
                    value={filterAufteilung.to[0]}
                    onChange={(e) => setAnEmail(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-white"
                    data-testid="composer-picker-filter-an"
                  >
                    {filterErgebnis.recipients.map((r) => (
                      <option key={r.email} value={r.email}>{r.name} · {r.email}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {ergebnisLaden ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Empfänger werden ermittelt …
                </div>
              ) : !filterErgebnis ? (
                <div className="text-center py-12 text-sm text-gray-400">Wähl einen Datenfilter, um die Empfänger zu sehen.</div>
              ) : filterErgebnis.fehler ? (
                <div className="text-center py-12 text-sm text-red-500">Der Datenfilter konnte nicht geladen werden.</div>
              ) : filterErgebnis.recipients.length === 0 ? (
                <div className="text-center py-12 text-sm text-gray-400">
                  Keine Adresse dieses Datenfilters hat eine E-Mail-Adresse.
                </div>
              ) : (
                <>
                  <div className="sticky top-0 z-10 px-4 py-2 bg-gray-50/95 backdrop-blur border-b border-gray-100 text-[11px] text-gray-500" data-testid="composer-picker-filter-info">
                    {filterErgebnis.total_addresses} Adressen · {filterErgebnis.recipients.length} Empfänger mit E-Mail
                    {filterErgebnis.ohne_email > 0 && ` · ${filterErgebnis.ohne_email} ohne E-Mail (werden übersprungen)`}
                  </div>
                  <ul className="divide-y divide-gray-100" data-testid="composer-picker-filter-results">
                    {filterErgebnis.recipients.map((r) => {
                      const istAn = filterAufteilung.to.includes(r.email);
                      return (
                        <li key={r.email} className="px-4 py-2 flex items-center gap-3">
                          <span className={`text-[10px] font-bold uppercase w-10 text-center px-1.5 py-0.5 rounded-full shrink-0 ${
                            istAn ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {istAn ? 'An' : 'BCC'}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">{r.name}</div>
                            <div className="text-xs text-gray-500 truncate flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>

            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/60">
              <div className="text-xs text-gray-500 flex items-start gap-1.5 min-w-0" data-testid="composer-picker-filter-hinweis">
                <EyeOff className="w-3.5 h-3.5 shrink-0 mt-px" />
                <span>
                  {hatAnEmpfaenger
                    ? 'Im „An“ steht schon ein Empfänger – alle Treffer kommen ins BCC.'
                    : 'BCC-Empfänger sehen sich nicht gegenseitig; die „An“-Adresse ist für alle sichtbar.'}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleFilterUebernehmen}
                  disabled={ergebnisLaden || (!filterAufteilung.to.length && !filterAufteilung.bcc.length)}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                  data-testid="composer-picker-filter-add"
                >
                  {filterAufteilung.to.length + filterAufteilung.bcc.length > 0
                    ? `${filterAufteilung.to.length + filterAufteilung.bcc.length} Empfänger übernehmen`
                    : 'Übernehmen'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
        {/* Search + Target */}
        <div className="px-4 py-2 border-b border-gray-100 space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filtern nach Name, Firma oder E-Mail…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
              data-testid="composer-picker-search"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mr-1">Hinzufügen zu:</span>
            {TARGETS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTarget(t.key)}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  target === t.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid={`composer-picker-target-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {!loading && filteredOptions.length > 0 && (
            <div className="sticky top-0 z-10 px-4 py-2 bg-gray-50/95 backdrop-blur border-b border-gray-100 flex items-center justify-between">
              <span className="text-[11px] text-gray-500">{filteredOptions.length} Empfänger mit E-Mail</span>
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                data-testid="composer-picker-select-all"
              >
                {allFilteredSelected ? 'Auswahl aufheben' : 'Alle auswählen'}
              </button>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Adressen werden geladen …
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              {query.trim() ? `Keine Treffer für „${query}".` : 'Keine Adressen mit E-Mail-Adresse gefunden.'}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="composer-picker-results">
              {filteredOptions.map((opt) => {
                const isSelected = !!selected[opt.email];
                const Icon = opt.icon === 'company' ? Building2 : User;
                return (
                  <li key={`${opt.email}-${opt.addressId}`}>
                    <button
                      type="button"
                      onClick={() => toggle(opt)}
                      className={`w-full text-left px-4 py-2 flex items-center gap-3 transition-colors ${
                        isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`composer-picker-row-${opt.email}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="w-4 h-4 text-blue-600 rounded shrink-0"
                      />
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                        opt.isContactPerson ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1">
                          {opt.label}
                          {opt.isContactPerson && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">AP</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {opt.email} <ChevronRight className="w-3 h-3" /> {opt.subline}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/60">
          <div className="text-xs text-gray-500" data-testid="composer-picker-count">
            {selectedCount > 0 ? `${selectedCount} ausgewählt` : 'Keine Auswahl'}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={selectedCount === 0}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              data-testid="composer-picker-add"
            >
              {selectedCount > 0 ? `${selectedCount} zu „${TARGETS.find(t => t.key === target)?.label}" hinzufügen` : 'Hinzufügen'}
            </button>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
